import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getAuthHeader } from './leadsConstants';
import { sendWhatsApp } from '@/utils/whatsappHelper';

export function ProformaModal({ lead, onClose }) {
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [vehiclePrices, setVehiclePrices] = React.useState([]);
  const [accessories, setAccessories] = React.useState([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState(null);
  const [selectedAccessories, setSelectedAccessories] = React.useState([]);
  const [proforma, setProforma] = React.useState(null);
  const [company, setCompany] = React.useState(null);
  const [customTerms, setCustomTerms] = React.useState('');
  const [step, setStep] = React.useState('select'); // 'select' | 'accessories' | 'deductions' | 'preview' | 'saved'

  // Brand/Model filter for Step 1 vehicle selection
  const [brandFilter, setBrandFilter] = React.useState('');
  const [modelFilter, setModelFilter] = React.useState('');

  // Saved proformas for this lead
  const [savedProformas, setSavedProformas] = React.useState([]);
  
  // Editable deduction values
  const [deductions, setDeductions] = React.useState({
    pme_drive: 0,
    price_benefit: 0,
    discount: 0
  });

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch each independently so one failure doesn't break others
      let prices = [];
      let accs = [];
      
      try {
        const pricesRes = await axios.get(`${BACKEND_URL}/api/masters/vehicle-prices`, getAuthHeader());
        prices = pricesRes.data || [];
      } catch (e) {
        console.error('Error fetching vehicle prices:', e);
        toast.error('Failed to load vehicle prices');
      }
      
      try {
        const accessoriesRes = await axios.get(`${BACKEND_URL}/api/masters?type=accessory`, getAuthHeader());
        accs = accessoriesRes.data || [];
      } catch (e) {
        console.error('Error fetching accessories:', e);
      }
      
      setVehiclePrices(prices);
      setAccessories(accs);
      
      // Fetch company details from masters
      try {
        const mastersRes = await axios.get(`${BACKEND_URL}/api/masters?type=company`, getAuthHeader());
        if (mastersRes.data && mastersRes.data.length > 0) {
          setCompany(mastersRes.data[0]);
        }
      } catch (e) {
        console.log('Company info not available');
      }
      
      // Fetch saved proformas for this lead
      try {
        const savedRes = await axios.get(`${BACKEND_URL}/api/proforma-invoice/lead/${lead.id}`, getAuthHeader());
        setSavedProformas(savedRes.data || []);
      } catch (e) {
        console.log('No saved proformas');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(amount));
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Set default deduction values from vehicle price
    setDeductions({
      pme_drive: vehicle.pme_drive || 0,
      price_benefit: vehicle.price_benefits || 0,
      discount: vehicle.discounts || 0
    });
    setStep('accessories');
  };

  const toggleAccessory = (accessory) => {
    setSelectedAccessories(prev => {
      const exists = prev.find(a => a.id === accessory.id);
      if (exists) {
        return prev.filter(a => a.id !== accessory.id);
      }
      return [...prev, accessory];
    });
  };

  const handleDeductionChange = (field, value) => {
    setDeductions(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const generateProforma = async () => {
    if (!selectedVehicle) {
      toast.error('Please select a vehicle');
      return;
    }
    
    setGenerating(true);
    try {
      const payload = {
        lead_id: lead.id,
        brand_name: selectedVehicle.brand_name,
        model: selectedVehicle.model,
        variant: selectedVehicle.variant,
        color: selectedVehicle.color || '',
        customer_name: `${lead.salutation || ''} ${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
        customer_mobile: lead.mobile || '',
        customer_email: lead.email || '',
        customer_address: [lead.door_no, lead.address_1, lead.address_2, lead.area, lead.city, lead.state, lead.pin_code].filter(Boolean).join(', '),
        selected_accessories: selectedAccessories.map(a => ({ id: a.id, name: a.name, price: a.price || 0 })),
        custom_terms: customTerms,
        custom_pme_drive: deductions.pme_drive,
        custom_price_benefit: deductions.price_benefit,
        custom_discount: deductions.discount,
        include_insurance: true,
        include_mandatory_accessories: true,
        include_registration: true,
        include_handling: true,
        include_b2b: true,
        include_bpp: true,
        include_rsa: true,
        include_pme_drive: deductions.pme_drive > 0,
        include_baas: true,
        include_price_benefits: deductions.price_benefit > 0,
        include_discounts: deductions.discount > 0
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/proforma-invoice/generate`, payload, getAuthHeader());
      setProforma({ ...response.data, company, selectedAccessories, customDeductions: deductions });
      setStep('preview');
    } catch (error) {
      console.error('Error generating proforma:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate proforma');
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleWhatsAppShare = async () => {
    const phone = lead.mobile?.replace(/[^0-9]/g, '');
    if (!phone) {
      toast.error('No mobile number available');
      return;
    }
    const customerName = `${lead.salutation || ''} ${lead.first_name || 'Customer'}`.trim();
    const message = `Dear ${customerName},\n\nPlease find the Proforma Invoice for ${selectedVehicle?.brand_name} ${selectedVehicle?.model}.\n\nNet On-Road Price: ${formatCurrency(calculateNetOnRoadPrice())}\n\nThank you for your interest!`;
    const fullPhone = phone.startsWith('91') ? phone : '91' + phone;
    const result = await sendWhatsApp(fullPhone, message);
    if (result.status === 'failed') toast.error(result.error || 'Failed to send');
    else if (result.mode === 'api') toast.success('Proforma sent via WhatsApp!');
  };

  const handleSaveProforma = async () => {
    if (!proforma) return;
    
    try {
      const payload = {
        lead_id: lead.id,
        proforma_data: {
          ...proforma,
          customer: {
            ...proforma.customer,
            salutation: lead.salutation || '',
            name: `${lead.salutation || ''} ${lead.first_name || ''} ${lead.last_name || ''}`.trim()
          },
          selectedAccessories,
          customDeductions: deductions,
          netOnRoadPrice: calculateNetOnRoadPrice()
        }
      };
      
      await axios.post(`${BACKEND_URL}/api/proforma-invoice/save`, payload, getAuthHeader());
      toast.success('Proforma saved successfully');
      
      // Refresh saved list
      const savedRes = await axios.get(`${BACKEND_URL}/api/proforma-invoice/lead/${lead.id}`, getAuthHeader());
      setSavedProformas(savedRes.data || []);
    } catch (error) {
      toast.error('Failed to save proforma');
    }
  };

  const handleDeleteProforma = async (proformaId) => {
    if (!window.confirm('Are you sure you want to delete this proforma invoice?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/proforma-invoice/${proformaId}`, getAuthHeader());
      toast.success('Proforma deleted successfully');
      
      // Refresh saved list
      const savedRes = await axios.get(`${BACKEND_URL}/api/proforma-invoice/lead/${lead.id}`, getAuthHeader());
      setSavedProformas(savedRes.data || []);
    } catch (error) {
      toast.error('Failed to delete proforma');
    }
  };

  const handleViewSavedProforma = async (savedProforma) => {
    // Load the saved proforma data
    setProforma(savedProforma);
    setSelectedAccessories(savedProforma.selectedAccessories || []);
    setDeductions(savedProforma.customDeductions || { pme_drive: 0, price_benefit: 0, discount: 0 });
    
    // Find the matching vehicle
    const vehicle = vehiclePrices.find(v => 
      v.brand_name === savedProforma.vehicle?.brand &&
      v.model === savedProforma.vehicle?.model
    );
    if (vehicle) {
      setSelectedVehicle(vehicle);
    }
    
    setStep('preview');
  };

  const handleEditProforma = (savedProforma) => {
    // Load saved data into editing state and go to deductions step
    setProforma(savedProforma);
    setSelectedAccessories(savedProforma.selectedAccessories || []);
    setDeductions(savedProforma.customDeductions || { pme_drive: 0, price_benefit: 0, discount: 0 });
    
    const vehicle = vehiclePrices.find(v => 
      v.brand_name === savedProforma.vehicle?.brand &&
      v.model === savedProforma.vehicle?.model
    );
    if (vehicle) {
      setSelectedVehicle(vehicle);
    }
    
    setStep('deductions');
  };

  // Calculate totals for preview - all amounts rounded to nearest rupee
  const calculateTotals = () => {
    if (!selectedVehicle) return {};
    
    const v = selectedVehicle;
    const cgst = Math.round((v.ex_showroom_price || 0) * (v.cgst_percent || 0) / 100);
    const sgst = Math.round((v.ex_showroom_price || 0) * (v.sgst_percent || 0) / 100);
    
    const vehiclePriceTotal = Math.round((v.ex_showroom_price || 0) + cgst + sgst);
    
    const insuranceTotal = Math.round((v.comprehensive_insurance || 0) + (v.b2b || 0) + 
                          (v.bpp_battery_protection || 0) + (v.rsa_road_side_assistance || 0) + 
                          (v.baas || 0));
    
    const miscTotal = Math.round((v.mandatory_accessories || 0) + (v.registration_expenses || 0) + 
                     (v.handling_incidental_expenses || 0));
    
    const accessoriesTotal = Math.round(selectedAccessories.reduce((sum, a) => sum + (a.price || 0), 0));
    
    const deductionsTotal = Math.round((deductions.pme_drive || 0) + (deductions.price_benefit || 0) + 
                           (deductions.discount || 0));
    
    return { vehiclePriceTotal, insuranceTotal, miscTotal, accessoriesTotal, deductionsTotal, cgst, sgst };
  };

  const calculateNetOnRoadPrice = () => {
    const totals = calculateTotals();
    return Math.round((totals.vehiclePriceTotal || 0) + (totals.insuranceTotal || 0) + 
           (totals.miscTotal || 0) + (totals.accessoriesTotal || 0) - (totals.deductionsTotal || 0));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 sm:pt-10 overflow-y-auto">
        <div className="bg-white rounded-2xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-2 pt-4 sm:pt-6 print:p-0 print:bg-white overflow-y-auto print:overflow-visible">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        {/* Header - Hide on Print */}
        <div className="print:hidden p-4 border-b bg-gradient-to-r from-teal-600 to-emerald-600 text-white flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Generate Proforma Invoice</h2>
            <p className="text-sm text-white/80">Lead: #{lead.lead_no} - {lead.salutation || ''} {lead.first_name} {lead.last_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {savedProformas.length > 0 && step !== 'saved' && (
              <button 
                onClick={() => setStep('saved')}
                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Saved ({savedProformas.length})
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Saved Proformas List */}
        {step === 'saved' && (
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">Saved Proforma Invoices</h3>
              <button onClick={() => setStep('select')} className="text-sm text-teal-600 hover:underline">
                + Create New
              </button>
            </div>
            
            {savedProformas.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No saved proformas</p>
            ) : (
              <div className="space-y-3">
                {savedProformas.map(sp => (
                  <div key={sp.id} className="p-4 border rounded-xl hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-800">
                          {sp.vehicle?.brand} {sp.vehicle?.model} - {sp.vehicle?.variant}
                        </p>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(sp.created_at).toLocaleDateString('en-IN')} | 
                          Total: {formatCurrency(sp.netOnRoadPrice || sp.grand_total)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewSavedProforma(sp)}
                          className="p-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200"
                          title="View"
                          data-testid={`view-proforma-${sp.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEditProforma(sp)}
                          className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                          title="Edit"
                          data-testid={`edit-proforma-${sp.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteProforma(sp.id)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          title="Delete"
                          data-testid={`delete-proforma-${sp.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1: Vehicle Selection */}
        {step === 'select' && (
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            <h3 className="font-semibold text-gray-700 mb-3">Step 1: Select Vehicle</h3>
            {vehiclePrices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No vehicle prices configured</p>
                <p className="text-sm mt-2">Please add prices in Vehicle Price Master</p>
              </div>
            ) : (
              <>
                {/* Brand → Model filter */}
                {(() => {
                  const activePrices = vehiclePrices.filter(p => p.is_active !== false);
                  const brands = Array.from(new Set(activePrices.map(p => p.brand_name).filter(Boolean))).sort();
                  const models = brandFilter
                    ? Array.from(new Set(activePrices.filter(p => p.brand_name === brandFilter).map(p => p.model).filter(Boolean))).sort()
                    : [];
                  const isFiltered = !!(brandFilter || modelFilter);
                  return (
                    <div className="sticky top-0 z-10 bg-white -mt-1 mb-4 pb-3 border-b">
                      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Brand</label>
                          <select
                            data-testid="proforma-filter-brand"
                            value={brandFilter}
                            onChange={(e) => { setBrandFilter(e.target.value); setModelFilter(''); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="">All Brands</option>
                            {brands.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Model</label>
                          <select
                            data-testid="proforma-filter-model"
                            value={modelFilter}
                            onChange={(e) => setModelFilter(e.target.value)}
                            disabled={!brandFilter}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">{brandFilter ? 'All Models' : 'Select Brand first'}</option>
                            {models.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        {isFiltered && (
                          <button
                            type="button"
                            onClick={() => { setBrandFilter(''); setModelFilter(''); }}
                            data-testid="proforma-filter-clear"
                            className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg whitespace-nowrap"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vehiclePrices
                    .filter(p => p.is_active !== false)
                    .filter(p => !brandFilter || p.brand_name === brandFilter)
                    .filter(p => !modelFilter || p.model === modelFilter)
                    .map(price => (
                    <button
                      key={price.id}
                      onClick={() => handleVehicleSelect(price)}
                      className="p-4 border-2 rounded-xl text-left transition-all hover:border-teal-500 hover:shadow-md"
                    >
                      <h4 className="font-bold text-gray-800">{price.brand_name}</h4>
                      <p className="text-sm text-gray-600">{price.model} - {price.variant}</p>
                      {price.color && <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs">{price.color}</span>}
                      <div className="mt-2 pt-2 border-t">
                        <span className="text-xs text-gray-500">Ex-Showroom</span>
                        <p className="font-bold text-teal-700">{formatCurrency(price.ex_showroom_price)}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {(() => {
                  const visibleCount = vehiclePrices
                    .filter(p => p.is_active !== false)
                    .filter(p => !brandFilter || p.brand_name === brandFilter)
                    .filter(p => !modelFilter || p.model === modelFilter).length;
                  if ((brandFilter || modelFilter) && visibleCount === 0) {
                    return (
                      <p className="text-center text-sm text-gray-500 py-6" data-testid="proforma-filter-no-results">
                        No vehicles match the selected filters.
                      </p>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </div>
        )}

        {/* Step 2: Accessories Selection */}
        {step === 'accessories' && (
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-700">Step 2: Select Accessories</h3>
                <p className="text-sm text-gray-500">Vehicle: {selectedVehicle?.brand_name} {selectedVehicle?.model}</p>
              </div>
              <button onClick={() => setStep('select')} className="text-sm text-teal-600 hover:underline">
                ← Change Vehicle
              </button>
            </div>
            
            {accessories.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No accessories available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {accessories.map(acc => (
                  <label
                    key={acc.id}
                    className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedAccessories.find(a => a.id === acc.id)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!selectedAccessories.find(a => a.id === acc.id)}
                        onChange={() => toggleAccessory(acc)}
                        className="w-4 h-4 text-teal-600 rounded"
                      />
                      <span className="font-medium text-gray-800">{acc.name}</span>
                    </div>
                    <span className="text-teal-700 font-semibold">{formatCurrency(acc.price || 0)}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Selected Summary */}
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">
                Selected Accessories: {selectedAccessories.length} | 
                Total: {formatCurrency(selectedAccessories.reduce((sum, a) => sum + (a.price || 0), 0))}
              </p>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setStep('select')} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">
                Back
              </button>
              <button
                onClick={() => setStep('deductions')}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Next: Deductions →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Deductions (Editable) */}
        {step === 'deductions' && (
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-700">Step 3: Enter Deductions</h3>
                <p className="text-sm text-gray-500">Customize deduction values for this customer</p>
              </div>
              <button onClick={() => setStep('accessories')} className="text-sm text-teal-600 hover:underline">
                ← Back to Accessories
              </button>
            </div>
            
            <div className="space-y-4">
              {/* PME Drive */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PME Drive
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">₹</span>
                  <input
                    type="number"
                    value={deductions.pme_drive}
                    onChange={(e) => handleDeductionChange('pme_drive', e.target.value)}
                    className="flex-1 p-2 border rounded-lg text-right font-medium"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Default from price master: {formatCurrency(selectedVehicle?.pme_drive || 0)}</p>
              </div>

              {/* Price Benefit */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Benefit
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">₹</span>
                  <input
                    type="number"
                    value={deductions.price_benefit}
                    onChange={(e) => handleDeductionChange('price_benefit', e.target.value)}
                    className="flex-1 p-2 border rounded-lg text-right font-medium"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Default from price master: {formatCurrency(selectedVehicle?.price_benefits || 0)}</p>
              </div>

              {/* Discount */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">₹</span>
                  <input
                    type="number"
                    value={deductions.discount}
                    onChange={(e) => handleDeductionChange('discount', e.target.value)}
                    className="flex-1 p-2 border rounded-lg text-right font-medium"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Default from price master: {formatCurrency(selectedVehicle?.discounts || 0)}</p>
              </div>
            </div>

            {/* Deductions Total */}
            <div className="mt-4 p-3 bg-red-100 rounded-lg border border-red-300">
              <p className="text-sm font-bold text-red-800">
                Total Deductions: - {formatCurrency(deductions.pme_drive + deductions.price_benefit + deductions.discount)}
              </p>
            </div>

            {/* Custom Terms */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Terms & Conditions (Optional)</label>
              <textarea
                value={customTerms}
                onChange={(e) => setCustomTerms(e.target.value)}
                placeholder="Add any special terms..."
                className="w-full p-2 border rounded-lg text-sm"
                rows={2}
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setStep('accessories')} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">
                Back
              </button>
              <button
                onClick={generateProforma}
                disabled={generating}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {generating ? 'Generating...' : 'Generate Proforma →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Print */}
        {step === 'preview' && proforma && selectedVehicle && (
          <div className="overflow-y-auto max-h-[85vh] print:max-h-none print:overflow-visible">
            {/* Print Controls - Hide on Print */}
            <div className="print:hidden p-3 border-b bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
              <button onClick={() => setStep('deductions')} className="text-sm text-teal-600 hover:underline">
                ← Back to Deductions
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveProforma}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save
                </button>
                <button
                  onClick={handleWhatsAppShare}
                  className="px-4 py-2 text-white rounded-xl font-medium text-sm flex items-center gap-2 shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print / PDF
                </button>
                <button onClick={onClose} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 text-sm">
                  Close
                </button>
              </div>
            </div>

            {/* A4 Proforma Invoice - Optimized for Print */}
            <div className="p-3 print:p-0" style={{ minHeight: '297mm' }}>
              <div className="max-w-[210mm] mx-auto bg-white print:max-w-none" style={{ fontSize: '10px' }}>
                {/* Company Letterhead - Replicated from Sales Module */}
                <div className="pb-2 border-b-2 border-gray-800 mb-2">
                  <div className="flex justify-between items-start gap-4">
                    {/* Left: Company Details */}
                    <div className="flex-1">
                      <h1 className="text-lg font-bold text-gray-900 uppercase tracking-wide" data-testid="lead-proforma-company-name">
                        {company?.name || proforma.company_name || 'Dealership'}
                      </h1>
                      {company?.hashtag && (
                        <p className="text-[10px] text-teal-600 font-medium italic mt-0.5">{company.hashtag}</p>
                      )}
                      
                      <div className="mt-1.5 space-y-0.5 text-[10px] text-gray-600">
                        {(company?.door_no || company?.building_name || company?.address_1) && (
                          <p>{[company?.door_no, company?.building_name, company?.address_1].filter(Boolean).join(', ')}</p>
                        )}
                        {(company?.area || company?.city || company?.state || company?.pincode) && (
                          <p>{[company?.area, company?.city, company?.state, company?.pincode && `- ${company.pincode}`].filter(Boolean).join(', ')}</p>
                        )}

                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[9px] text-gray-500">
                          {company?.mobile && (
                            <span>Ph: {company.mobile}{company?.mobile_2 ? ` / ${company.mobile_2}` : ''}</span>
                          )}
                          {company?.landline_1 && (
                            <span>Tel: {company.landline_1}</span>
                          )}
                          {company?.email && (
                            <span>Email: {company.email}</span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-[9px] text-gray-500">
                          {company?.gst_no && <span><strong>GSTIN:</strong> {company.gst_no}</span>}
                          {company?.uin_no && <span><strong>UIN:</strong> {company.uin_no}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Right: Company Logo */}
                    {company?.logo && (
                      <div className="flex-shrink-0" data-testid="lead-proforma-company-logo">
                        <img
                          src={company.logo}
                          alt="Company Logo"
                          className="w-20 h-20 object-contain rounded border border-gray-200 print:w-16 print:h-16"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Title - No Invoice # or Valid Till */}
                <div className="text-center mb-2">
                  <h2 className="text-sm font-bold text-teal-700 border-2 border-teal-600 inline-block px-4 py-0.5" data-testid="lead-proforma-title">PROFORMA INVOICE</h2>
                </div>

                {/* Invoice Info Row - Only Date & Lead No */}
                <div className="flex justify-between mb-2 text-[10px]">
                  <div>
                    <p><strong>Date:</strong> {proforma.invoice_date || new Date().toLocaleDateString('en-IN')}</p>
                    <p><strong>Lead No:</strong> #{lead.lead_no}</p>
                  </div>
                  <div className="text-right">
                    <p><strong>Customer:</strong> {lead.salutation || ''} {lead.first_name || ''} {lead.last_name || ''}</p>
                    <p><strong>Mobile:</strong> {lead.mobile || '-'}</p>
                    {lead.email && <p><strong>Email:</strong> {lead.email}</p>}
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="bg-slate-100 p-1.5 rounded mb-2">
                  <p className="font-bold text-xs">{selectedVehicle.brand_name} {selectedVehicle.model} - {selectedVehicle.variant}</p>
                  {selectedVehicle.color && <p className="text-[10px] text-gray-600">Color: {selectedVehicle.color}</p>}
                </div>

                {/* TABLE 1: Vehicle Price & Taxes */}
                <table className="w-full border-collapse text-[10px] mb-2">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="border border-gray-400 p-1 text-left font-bold" colSpan={2}>1. VEHICLE PRICE & TAXES</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-blue-50">
                      <td className="border border-gray-400 p-1">Ex-Showroom Price</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.ex_showroom_price)}</td>
                    </tr>
                    <tr className="bg-blue-50/70">
                      <td className="border border-gray-400 p-1">CGST @ {selectedVehicle.cgst_percent || 0}%</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(totals.cgst)}</td>
                    </tr>
                    <tr className="bg-blue-50/50">
                      <td className="border border-gray-400 p-1">SGST @ {selectedVehicle.sgst_percent || 0}%</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(totals.sgst)}</td>
                    </tr>
                    <tr className="bg-blue-200 font-bold">
                      <td className="border border-gray-400 p-1">Total Vehicle Price & Taxes</td>
                      <td className="border border-gray-400 p-1 text-right w-28">{formatCurrency(totals.vehiclePriceTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* TABLE 2: Insurance Details */}
                <table className="w-full border-collapse text-[10px] mb-2">
                  <thead>
                    <tr className="bg-yellow-100">
                      <th className="border border-gray-400 p-1 text-left font-bold" colSpan={2}>2. INSURANCE DETAILS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-yellow-50">
                      <td className="border border-gray-400 p-1">Comprehensive Insurance</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.comprehensive_insurance)}</td>
                    </tr>
                    <tr className="bg-yellow-50/70">
                      <td className="border border-gray-400 p-1">B2B</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.b2b)}</td>
                    </tr>
                    <tr className="bg-yellow-50/50">
                      <td className="border border-gray-400 p-1">BPP (Battery Protection Cover)</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.bpp_battery_protection)}</td>
                    </tr>
                    <tr className="bg-yellow-50/70">
                      <td className="border border-gray-400 p-1">RSA (Road Side Assistance)</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.rsa_road_side_assistance)}</td>
                    </tr>
                    <tr className="bg-yellow-50/50">
                      <td className="border border-gray-400 p-1">BAAS</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.baas)}</td>
                    </tr>
                    <tr className="bg-yellow-200 font-bold">
                      <td className="border border-gray-400 p-1">Total Insurance Details</td>
                      <td className="border border-gray-400 p-1 text-right w-28">{formatCurrency(totals.insuranceTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* TABLE 3: Miscellaneous Charges */}
                <table className="w-full border-collapse text-[10px] mb-2">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="border border-gray-400 p-1 text-left font-bold" colSpan={2}>3. MISCELLANEOUS CHARGES</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-purple-50">
                      <td className="border border-gray-400 p-1">Mandatory Accessories</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.mandatory_accessories)}</td>
                    </tr>
                    <tr className="bg-purple-50/70">
                      <td className="border border-gray-400 p-1">Registration Expenses</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.registration_expenses)}</td>
                    </tr>
                    <tr className="bg-purple-50/50">
                      <td className="border border-gray-400 p-1">Handling & Incidental Expenses</td>
                      <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(selectedVehicle.handling_incidental_expenses)}</td>
                    </tr>
                    <tr className="bg-purple-200 font-bold">
                      <td className="border border-gray-400 p-1">Total Miscellaneous Charges</td>
                      <td className="border border-gray-400 p-1 text-right w-28">{formatCurrency(totals.miscTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* TABLE 4: Accessories */}
                <table className="w-full border-collapse text-[10px] mb-2">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="border border-gray-400 p-1 text-left font-bold" colSpan={2}>4. ACCESSORIES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAccessories.length === 0 ? (
                      <tr className="bg-green-50">
                        <td className="border border-gray-400 p-1 text-gray-500 italic" colSpan={2}>No accessories selected</td>
                      </tr>
                    ) : (
                      selectedAccessories.map((acc, idx) => (
                        <tr key={idx} className="bg-green-50">
                          <td className="border border-gray-400 p-1">{acc.name}</td>
                          <td className="border border-gray-400 p-1 text-right font-medium w-28">{formatCurrency(acc.price || 0)}</td>
                        </tr>
                      ))
                    )}
                    <tr className="bg-green-200 font-bold">
                      <td className="border border-gray-400 p-1">Total Accessories</td>
                      <td className="border border-gray-400 p-1 text-right w-28">{formatCurrency(totals.accessoriesTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* TABLE 5: Deductions */}
                <table className="w-full border-collapse text-[10px] mb-2">
                  <thead>
                    <tr className="bg-red-100">
                      <th className="border border-gray-400 p-1 text-left font-bold" colSpan={2}>5. DEDUCTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-red-50">
                      <td className="border border-gray-400 p-1">a. PME Drive</td>
                      <td className="border border-gray-400 p-1 text-right font-medium text-red-600 w-28">- {formatCurrency(deductions.pme_drive)}</td>
                    </tr>
                    <tr className="bg-red-50/70">
                      <td className="border border-gray-400 p-1">b. Price Benefit</td>
                      <td className="border border-gray-400 p-1 text-right font-medium text-red-600 w-28">- {formatCurrency(deductions.price_benefit)}</td>
                    </tr>
                    <tr className="bg-red-50/50">
                      <td className="border border-gray-400 p-1">c. Discount</td>
                      <td className="border border-gray-400 p-1 text-right font-medium text-red-600 w-28">- {formatCurrency(deductions.discount)}</td>
                    </tr>
                    <tr className="bg-red-200 font-bold">
                      <td className="border border-gray-400 p-1">Total Deductions</td>
                      <td className="border border-gray-400 p-1 text-right text-red-700 w-28">- {formatCurrency(totals.deductionsTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* NET ON-ROAD PRICE */}
                <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-3 rounded mb-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-sm">NET ON-ROAD PRICE</p>
                      <p className="text-[9px] text-white/80">{proforma.grand_total_words}</p>
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(calculateNetOnRoadPrice())}</p>
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div className="bg-gray-50 p-1.5 rounded text-[10px]">
                  <p className="font-semibold mb-0.5" style={{ fontSize: '10px' }}>Terms & Conditions:</p>
                  <div className="text-gray-600 whitespace-pre-line" style={{ fontSize: '8px' }}>
                    {customTerms || proforma.terms_and_conditions || `1. Prices are subject to change without prior notice.
2. This is a proforma invoice and not a tax invoice.
3. Booking amount is non-refundable.
4. Vehicle delivery subject to availability.
5. Insurance is calculated as per current rates.
6. Registration charges may vary.`}
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-2 pt-1 border-t text-center text-[8px] text-gray-400">
                  This is a computer-generated document. | Generated on {new Date().toLocaleString('en-IN')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles - Optimized for A4 */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .fixed { position: absolute !important; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:max-h-none { max-height: none !important; }
          .print\\:overflow-visible { overflow: visible !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          [class*="max-w-4xl"] * { visibility: visible; }
          [class*="max-w-4xl"] { position: absolute; left: 0; top: 0; width: 100%; }
          @page { size: A4; margin: 8mm; }
          table { page-break-inside: avoid; }
          tr { page-break-inside: avoid; }
          td, th { border-color: #555 !important; }
        }
      `}</style>
    </div>
  );
}

